import { LuxDialog, SpecRow } from "../common/LuxDialog";
import { LuxButton } from "../common/Primitives";
import { STEPS } from "../sections/Process";
import { useUI } from "../../context/UIContext";

const Block = ({ title, children }) => (
  <div className="border-t border-[#B18A4A]/25 pt-5">
    <h3 className="font-sans text-[11px] font-bold uppercase tracking-[0.24em] text-[#26231F]">{title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-[#746D64]">{children}</p>
  </div>
);

export const CoBrandDetails = () => {
  const { panel, closePanel, openEnquiry } = useUI();
  return (
    <LuxDialog open={panel === "cobrand"} onClose={closePanel} eyebrow="Hotel Co-Branding" title="Your identity, on every table" testid="cobrand-dialog">
      <div className="grid gap-6 sm:grid-cols-2">
        <Block title="Custom label concepts">We develop label concepts that sit alongside your property's identity with restraint.</Block>
        <Block title="Branding options">Options for hotels, restaurants and hospitality groups, from single outlets to multiple properties.</Block>
        <Block title="Artwork approval">Every label proceeds only after packaging review and your written artwork approval.</Block>
        <Block title="Your brand, your permission">We never show a partner's identity publicly without explicit consent.</Block>
      </div>
      <dl className="mt-8">
        <SpecRow label="Minimum order quantity" value="To be discussed" />
        <SpecRow label="Production lead time" value="To be confirmed" />
        <SpecRow label="Commercial terms" value="Available on enquiry" />
      </dl>
      <LuxButton arrow className="mt-8" onClick={() => openEnquiry("Hotel co-branding / private label")} data-testid="cobrand-dialog-enquire-btn">Discuss a co-branded bottle</LuxButton>
    </LuxDialog>
  );
};

export const SustainDetails = () => {
  const { panel, closePanel, openEnquiry } = useUI();
  return (
    <LuxDialog open={panel === "sustain"} onClose={closePanel} eyebrow="Sustainability" title="Our returnable-bottle approach" testid="sustain-dialog">
      <div className="space-y-6">
        <Block title="How it works">Bottles are delivered, enjoyed by guests, collected on an agreed schedule, then professionally washed, inspected, sanitised and refilled. Bottles that do not pass inspection are removed from circulation.</Block>
        <Block title="What determines the benefit">The real environmental benefit of returnable glass depends on collection distance, bottle return rates, washing energy and water, the number of reuse cycles achieved, and logistics. Glass is not automatically the better choice — it becomes a more considered one when the loop is run well.</Block>
        <Block title="Quarterly impact certificate">Each partner will receive a quarterly certificate estimating single-use plastic bottles avoided and carbon emissions saved, based on their actual returns and a disclosed calculation method.</Block>
        <Block title="Our position">Sustainability is an operational objective we work towards with partners, not a guaranteed outcome. We do not publish quantified claims without supporting evidence.</Block>
      </div>
      <LuxButton arrow className="mt-8" onClick={() => openEnquiry("Hotel supply partnership")} data-testid="sustain-dialog-enquire-btn">Discuss a returnable programme</LuxButton>
    </LuxDialog>
  );
};

export const ProcessDetails = () => {
  const { panel, closePanel } = useUI();
  return (
    <LuxDialog open={panel === "process"} onClose={closePanel} eyebrow="Our Process" title="Quality, traceability & testing" wide testid="process-dialog">
      <ol className="grid gap-6 md:grid-cols-2">
        {STEPS.map((s, i) => (
          <li key={s.title} className="flex gap-5 border-t border-[#B18A4A]/25 pt-5">
            <span className="font-serif-lux text-3xl leading-none text-[#B18A4A]">0{i + 1}</span>
            <div>
              <h3 className="font-sans text-[11px] font-bold uppercase tracking-[0.24em] text-[#26231F]">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#746D64]">{s.text}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <Block title="Why testing matters">Regular testing of source water and finished product is the foundation of consistent quality and regulatory compliance.</Block>
        <Block title="Batch traceability">Each batch is intended to carry a code linking it to its production records, tests and inspections.</Block>
        <Block title="Status">This is our intended process. Facility, licence and certification details will be published only once verified.</Block>
      </div>
    </LuxDialog>
  );
};
